import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getParrainageList, companiesForAdmins, toggleParrainageStatus } from '../../../api/redux/slices/SuperAdmin';
import { LoaderComponent } from '../../../helpers/components/loader';
import { convertDate, roundToThreeDecimals } from '../../../helpers/modules';
import { icons } from '../../../assets/icons';
import './index.scss';

const ParrainageList = () => {
    const dispatch = useDispatch();
    const { parrainages, loading, total, totalAmount, totalPaid, totalUnpaid, allCompanies } = useSelector((state) => state.superAdmin);
    const token = localStorage.getItem('accesstoken');
    
    const [page, setPage] = useState(1);
    const [sponsorFilter, setSponsorFilter] = useState('');
    const [beneficiaryFilter, setBeneficiaryFilter] = useState('');
    const [togglingId, setTogglingId] = useState(null);
    const limit = 6;

    useEffect(() => {
        dispatch(companiesForAdmins({ token }));
    }, [dispatch, token]);

    useEffect(() => {
        dispatch(getParrainageList({ 
            token, 
            page,
            sponsorCompany: sponsorFilter || undefined,
            beneficiaryCompany: beneficiaryFilter || undefined
        }));
    }, [dispatch, token, page, sponsorFilter, beneficiaryFilter]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSponsorFilter = (companyId) => {
        setSponsorFilter(companyId);
        setPage(1);
    };

    const handleBeneficiaryFilter = (companyId) => {
        setBeneficiaryFilter(companyId);
        setPage(1);
    };

    const handleToggleStatus = async (parrainageId) => {
        setTogglingId(parrainageId);
        try {
            await dispatch(toggleParrainageStatus({ token, id: parrainageId })).unwrap();
            // Refresh the list
            dispatch(getParrainageList({ 
                token, 
                page,
                sponsorCompany: sponsorFilter || undefined,
                beneficiaryCompany: beneficiaryFilter || undefined
            }));
        } catch (error) {
            console.error('Error toggling status:', error);
        } finally {
            setTogglingId(null);
        }
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div id="parrainageList">
            <div className="parrainage-header">
                <div className="header-content">
                    <div className="header-icon">
                        <icons.BsArrowLeftRight style={{ fontSize: '24px' }} />
                    </div>
                    <div className="header-text">
                        <h1>Parrainages</h1>
                        <p>Gestion des commissions de parrainage entre sociétés</p>
                    </div>
                </div>
                <div className="header-stats">
                    <div className="stat-card">
                        <span className="stat-label">Total</span>
                        <span className="stat-value">{total}</span>
                    </div>
                    <div className="stat-card highlight">
                        <span className="stat-label">Montant Total</span>
                        <span className="stat-value">{roundToThreeDecimals(totalAmount)} DT</span>
                    </div>
                    <div className="stat-card paid">
                        <span className="stat-label">Payé</span>
                        <span className="stat-value">{roundToThreeDecimals(totalPaid)} DT</span>
                    </div>
                    <div className="stat-card unpaid">
                        <span className="stat-label">Non Payé</span>
                        <span className="stat-value">{roundToThreeDecimals(totalUnpaid)} DT</span>
                    </div>
                </div>
            </div>

            {/* Filters Row */}
            <div className="filters-row">
                <div className="filter-group">
                    <label className="filter-label">
                        <icons.BsBuilding style={{ fontSize: '14px' }} />
                        Société Payeuse
                    </label>
                    <select 
                        value={sponsorFilter} 
                        onChange={(e) => handleSponsorFilter(e.target.value)}
                        className="filter-select sponsor-select"
                    >
                        <option value="">Toutes les sociétés</option>
                        {allCompanies?.map(company => (
                            <option key={company._id} value={company._id}>
                                {company.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label className="filter-label">
                        <icons.BsGift style={{ fontSize: '14px' }} />
                        Société Bénéficiaire
                    </label>
                    <select 
                        value={beneficiaryFilter} 
                        onChange={(e) => handleBeneficiaryFilter(e.target.value)}
                        className="filter-select beneficiary-select"
                    >
                        <option value="">Toutes les sociétés</option>
                        {allCompanies?.map(company => (
                            <option key={company._id} value={company._id}>
                                {company.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <LoaderComponent />
            ) : (
                <>
                    <div className="parrainage-grid">
                        {parrainages?.map((parrainage, index) => (
                            <div className="parrainage-card" key={parrainage._id || index}>
                                <div className="card-header">
                                    <span className="order-ref">
                                        <icons.BsReceipt />
                                        <span>{parrainage.orderReference || '-'}</span>
                                    </span>
                                    <div className="header-right">
                                        <button 
                                            className={`status-toggle ${parrainage.status === 'paid' ? 'paid' : 'unpaid'}`}
                                            onClick={() => handleToggleStatus(parrainage._id)}
                                            disabled={togglingId === parrainage._id}
                                            title={parrainage.status === 'paid' ? 'Marquer comme non payé' : 'Marquer comme payé'}
                                        >
                                            {togglingId === parrainage._id ? (
                                                <span className="toggling-spinner"></span>
                                            ) : parrainage.status === 'paid' ? (
                                                <icons.BsCheckCircle />
                                            ) : (
                                                <icons.BsXCircle />
                                            )}
                                            <span>{parrainage.status === 'paid' ? 'Payé' : 'Non Payé'}</span>
                                        </button>
                                        <span className="date">
                                            {convertDate(parrainage.createdAt, 'DD/MM/YYYY')}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="companies-flow">
                                    {/* Sponsor Company (Pays) */}
                                    <div className="company sponsor">
                                        <div className="company-logo">
                                            {parrainage.sponsorCompany?.image?.secure_url ? (
                                                <img 
                                                    src={parrainage.sponsorCompany.image.secure_url} 
                                                    alt={parrainage.sponsorCompanyName}
                                                />
                                            ) : (
                                                <div className="logo-placeholder sponsor-placeholder">
                                                    <icons.BsBuilding />
                                                </div>
                                            )}
                                        </div>
                                        <div className="company-info">
                                            <span className="company-name">
                                                {parrainage.sponsorCompanyName || '-'}
                                            </span>
                                            <span className="company-role">Société Payeuse</span>
                                        </div>
                                    </div>

                                    {/* Flow Arrow */}
                                    <div className="flow-section">
                                        <div className="arrow-container">
                                            <div className="arrow-line"></div>
                                            <div className="arrow-icon">
                                                <icons.BsArrowRight />
                                            </div>
                                        </div>
                                        <div className="amount-badge">
                                            <span className="amount">
                                                {roundToThreeDecimals(parrainage.price)} DT
                                            </span>
                                            <span className="percentage">10%</span>
                                        </div>
                                    </div>

                                    {/* Beneficiary Company (Receives) */}
                                    <div className="company beneficiary">
                                        <div className="company-logo">
                                            {parrainage.beneficiaryCompany?.image?.secure_url ? (
                                                <img 
                                                    src={parrainage.beneficiaryCompany.image.secure_url} 
                                                    alt={parrainage.beneficiaryCompanyName}
                                                />
                                            ) : (
                                                <div className="logo-placeholder beneficiary-placeholder">
                                                    <icons.BsBuilding />
                                                </div>
                                            )}
                                        </div>
                                        <div className="company-info">
                                            <span className="company-name">
                                                {parrainage.beneficiaryCompanyName || '-'}
                                            </span>
                                            <span className="company-role">Société Bénéficiaire</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {parrainages?.length === 0 && !loading && (
                        <div className="empty-state">
                            <icons.BsInbox style={{ fontSize: '48px', color: '#94a3b8' }} />
                            <h3>Aucun parrainage trouvé</h3>
                            <p>Aucune transaction de parrainage n'a été effectuée pour le moment.</p>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button 
                                className="page-btn"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                            >
                                <icons.BsChevronLeft />
                            </button>
                            
                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index}
                                    className={`page-btn ${page === index + 1 ? 'active' : ''}`}
                                    onClick={() => handlePageChange(index + 1)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                            
                            <button 
                                className="page-btn"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === totalPages}
                            >
                                <icons.BsChevronRight />
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ParrainageList;